import type { ReactNode } from 'react'
import { Seal } from './Seal'

/**
 * Every page is the same sheet of paper: a printed border, a filing band, a
 * letterhead, then whatever the page puts on the form. `stamp` goes in the
 * top-right of the letterhead, where a clerk would put it.
 *
 * The filing band names the form. It is DC-1 everywhere a complainant goes;
 * the department's own paperwork carries its own number.
 */
export function FormSheet({
  children,
  stamp,
  form = 'Form DC-1 (Rev. 10/2026)',
  control = 'OMB Control No. 0000-0001',
}: {
  children: ReactNode
  stamp?: ReactNode
  form?: string
  control?: string
}) {
  return (
    <div className="min-h-screen bg-desk px-3 py-6 sm:px-6 sm:py-12">
      <main className="mx-auto max-w-[46rem]">
        <div className="border border-carbon/40 bg-file p-[5px] shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
          <div className="overflow-hidden border border-carbon/20">
            <FilingBand form={form} control={control} />
            <Letterhead stamp={stamp} />
            {children}
          </div>
        </div>
        <p className="mx-auto mt-6 max-w-[34rem] text-center font-form text-[11px] uppercase leading-relaxed tracking-[0.14em] text-file/40">
          This department is not affiliated with any government.
          All complaints are reviewed by management.
        </p>
      </main>
    </div>
  )
}

function FilingBand({ form, control }: { form: string; control: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-carbon/20 px-4 py-1.5 font-form text-[10px] uppercase tracking-[0.16em] text-carbon/60 sm:px-7">
      <span>{form}</span>
      <span className="text-right">{control}</span>
    </div>
  )
}

function Letterhead({ stamp }: { stamp?: ReactNode }) {
  return (
    <header className="relative border-b-2 border-carbon/70 px-4 pb-5 pt-6 text-center sm:px-7">
      {stamp}
      <Seal className="mx-auto text-process" size={96} />
      <h1 className="mt-3 font-display text-[1.6rem] font-semibold uppercase leading-none tracking-[0.1em] text-carbon sm:text-[2.05rem]">
        Department of Complaints
      </h1>
    </header>
  )
}

/** A ruled band that opens a section. Numbered only where the form numbers it. */
export function SectionBand({ number, title }: { number?: number; title: string }) {
  return (
    <h2 className="flex items-baseline gap-3 bg-process px-4 py-1.5 font-form text-[11px] font-semibold uppercase tracking-[0.2em] text-file sm:px-7 sm:text-xs">
      {number !== undefined && (
        <>
          <span className="tabular-nums">Section {number}</span>
          <span aria-hidden="true" className="text-file/50">—</span>
        </>
      )}
      <span>{title}</span>
    </h2>
  )
}

/**
 * The block the form tells you not to write in. On the filing page it is
 * conspicuously empty; on the receipt it is where the department writes back.
 */
export function OfficialUseBlock({ children }: { children?: ReactNode }) {
  return (
    <section className="border-t-2 border-dashed border-carbon/35 px-4 py-4 sm:px-7">
      <div className="border border-process/45">
        <p className="border-b border-process/45 bg-process/10 px-3 py-1 font-form text-[10px] font-semibold uppercase tracking-[0.2em] text-process">
          For official use only
        </p>
        {children ? (
          <div className="px-3 py-4">{children}</div>
        ) : (
          <div className="official-hatch flex min-h-[84px] items-center justify-center px-3 py-4">
            <p className="font-form text-[11px] uppercase tracking-[0.22em] text-process/55">
              Do not write in this space
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
